import React, { useEffect, useRef, useState, CSSProperties } from 'react';
import styles from './index.module.less';
import Image from 'next/image';
import classNames from 'classnames';
import { Button } from 'antd';
import { HairstyleBloggerLabelModel } from '@/types/hairstyle';
import { imgUrl } from '@/shared/utils';
import { useConfigProvider } from '@/context/ConfigProvider';
import { useUserProvider } from '@/context/UserProvider';

// 自定义筛选器图标
const FilterIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="3" width="18" height="2" rx="1" fill="currentColor" />
    <rect x="1" y="15" width="18" height="2" rx="1" fill="currentColor" />
    <circle cx="6" cy="4" r="3" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="14" cy="16" r="3" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

export type LabelFilterAreaProps = {
  // 标签样式类型：图片样式或纯文本样式
  type: 'image' | 'text';
  // 标签数据列表
  labels: HairstyleBloggerLabelModel[] | string[];
  // 当前选中的标签项
  selectedItem?: string;
  // 是否显示更多按钮
  showMoreButton?: boolean;
  // 点击标签项回调
  onItemClick: (item: string) => void;
  // 点击更多按钮回调
  onMoreClick?: () => void;
  // 最大显示数量
  maxDisplayCount?: number;
  // 当点击需要登录的标签时的回调
  onLoginRequired?: () => void;
  // 是否需要登录（用于某些标签如"My Followed"）
  isLogin?: boolean;
  // 布局模式：滚动或换行
  layoutMode?: 'scroll' | 'wrap' | 'default';
  // 子元素类名
  className?: string;
  // 行内样式
  style?: React.CSSProperties;
  // 是否忽略大小写进行比较
  caseInsensitive?: boolean;
  // 是否在移动端启用固定顶部
  stickyOnMobile?: boolean;
  // 固定时的偏移量，用于适应顶部导航栏高度
  stickyOffset?: number;
  // 是否自动计算顶部偏移量（根据顶部提示栏状态）
  autoCalculateOffset?: boolean;
};

const LabelFilterArea: React.FC<LabelFilterAreaProps> = ({
  type,
  labels,
  selectedItem = '',
  showMoreButton = true,
  onItemClick,
  onMoreClick,
  maxDisplayCount = 10,
  onLoginRequired,
  isLogin = false,
  layoutMode = 'default',
  className = '',
  style,
  caseInsensitive = true,
  stickyOnMobile = false,
  stickyOffset = 69,
  autoCalculateOffset = true
}) => {
  const { isMobile } = useConfigProvider();
  const { isShowGiftView, isFirstCreation } = useUserProvider();
  const [isSticky, setIsSticky] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const placeholderRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState(0);
  const [currentOffset, setCurrentOffset] = useState(stickyOffset);
  // 获取显示的标签项，根据最大显示数量截取
  const displayLabels = labels?.length > maxDisplayCount ? labels.slice(0, maxDisplayCount) : labels;

  // 根据布局模式确定样式
  const getContainerClassName = () => {
    const baseClass = type === 'image' ? styles.imgLabelsList : styles.labelsList;
    if (layoutMode === 'scroll') {
      return classNames(baseClass, styles.scrollable, className);
    } else if (layoutMode === 'wrap') {
      return classNames(baseClass, styles.wrapped, className);
    }

    return classNames(baseClass, className);
  };

  // 判断项是否被选中，支持大小写不敏感模式
  const isItemSelected = (itemLabel: string) => {
    if (!selectedItem) return false;
    if (caseInsensitive) {
      return itemLabel?.toLocaleLowerCase() === selectedItem?.toLocaleLowerCase();
    }
    return itemLabel === selectedItem;
  };

  // 计算当前偏移量（考虑顶部提示栏）
  useEffect(() => {
    if (!autoCalculateOffset) return;

    // 导航栏高度
    const navHeight = 69;
    // 提示栏高度
    const tipsHeight = 52;

    // 如果显示顶部提示或是首次创建，添加提示栏高度
    if ((isLogin && isFirstCreation) || (!isLogin && isShowGiftView)) {
      setCurrentOffset(navHeight + tipsHeight);
    } else {
      setCurrentOffset(navHeight);
    }
  }, [isShowGiftView, isFirstCreation, isLogin, autoCalculateOffset, stickyOffset]);

  /**
   *
   * 当 `stickyOnMobile` 设置为 `true` 时，组件将在移动端滚动时固定到顶部。组件会自动处理以下情况：
   *
   * 1. 顶部导航栏（高度 69px）
   * 2. 顶部提示栏（高度 52px，仅在以下情况显示）：
   *   - 用户未登录且 `isShowGiftView` 为 `true`
   *   - 用户已登录且 `isFirstCreation` 为 `true`
   *
   * 当 `autoCalculateOffset` 为 `true` 时，组件将自动从 `UserProvider` 获取 `isShowGiftView` 和 `isFirstCreation` 状态，并实时调整固定位置。
   *
   * 如果需要手动控制偏移量，可将 `autoCalculateOffset` 设置为 `false`，并通过 `stickyOffset` 属性指定固定位置的偏移量。
   */
  useEffect(() => {
    if (!stickyOnMobile || !isMobile || !containerRef.current) return;

    // 保存容器原始高度，用于占位元素
    const updateContainerHeight = () => {
      if (containerRef.current) {
        setContainerHeight(containerRef.current.offsetHeight);
      }
    };

    updateContainerHeight();

    // 初始位置记录
    let initialTopPosition = 0;

    // 记录初始位置，用于正确计算sticky状态
    const recordInitialPosition = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      initialTopPosition = rect.top + window.pageYOffset;
    };

    // 在组件挂载后记录初始位置
    recordInitialPosition();

    const checkSticky = () => {
      if (!containerRef.current) return;

      // 使用滚动位置与初始位置比较，更可靠地判断是否应该sticky
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const shouldBeSticky = scrollTop + currentOffset >= initialTopPosition;

      if (shouldBeSticky !== isSticky) {
        setIsSticky(shouldBeSticky);
        // 当状态变化时更新高度，确保占位元素高度准确
        updateContainerHeight();
      }
    };

    // 使用requestAnimationFrame优化性能
    let ticking = false;
    const throttledCheckSticky = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          checkSticky();
          ticking = false;
        });
        ticking = true;
      }
    };

    // 添加passive: true来提高滚动性能
    window.addEventListener('scroll', throttledCheckSticky, { passive: true });

    // 在resize事件时重新记录初始位置并检查
    const handleResize = () => {
      recordInitialPosition();
      updateContainerHeight();
      throttledCheckSticky();
    };

    window.addEventListener('resize', handleResize, { passive: true });

    // DOM变化时也需要重新计算
    const mutationObserver = new MutationObserver(() => {
      setTimeout(() => {
        updateContainerHeight();
        recordInitialPosition();
        checkSticky();
      }, 0);
    });

    if (containerRef.current) {
      mutationObserver.observe(containerRef.current, {
        childList: true,
        subtree: true,
        attributes: true
      });
    }

    // 初始检查
    checkSticky();

    // 如果组件初始挂载时已经应该是sticky，确保正确设置
    if (initialTopPosition <= currentOffset) {
      setIsSticky(true);
      updateContainerHeight();
    }

    return () => {
      window.removeEventListener('scroll', throttledCheckSticky);
      window.removeEventListener('resize', handleResize);
      mutationObserver.disconnect();
    };
  }, [isMobile, stickyOnMobile, currentOffset, isSticky]);

  const containerStyle: CSSProperties = {
    ...style,
    display: 'flex',
    alignItems: 'center',
    marginBottom: '8px',
    width: '100%',
    ...(isSticky && isMobile && stickyOnMobile
      ? {
          position: 'fixed',
          top: `${currentOffset}px`,
          left: 0,
          right: 0,
          zIndex: 100,
          backgroundColor: 'var(--background-rgb, #1a1a1a)',
          padding: '8px 16px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
        }
      : {})
  };

  return (
    <>
      {/* 占位元素，仅在sticky模式激活时可见 */}
      {isMobile && stickyOnMobile && (
        <div
          ref={placeholderRef}
          style={{
            height: isSticky ? `${containerHeight}px` : 0,
            width: '100%',
            visibility: isSticky ? 'visible' : 'hidden',
            display: isSticky ? 'block' : 'none',
            position: isSticky ? 'static' : 'absolute',
            pointerEvents: 'none'
          }}
          aria-hidden="true"
        />
      )}
      <div ref={containerRef} style={containerStyle} className={className}>
        {layoutMode === 'scroll' && showMoreButton && isMobile && (
          <Button
            onClick={onMoreClick}
            icon={<FilterIcon />}
            style={{
              borderRadius: '50%',
              border: 'none',
              color: 'white',
              backgroundColor: '#9873FF',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              marginRight: '10px',
              minWidth: '40px',
              flexShrink: 0
            }}
          />
        )}
        <div className={getContainerClassName()} style={{ width: '100%' }}>
          {displayLabels?.map((item) => {
            // 处理不同类型的标签项
            const label = typeof item === 'string' ? item : item.label;

            if (type === 'image') {
              // 图片样式标签
              const imageItem = item as HairstyleBloggerLabelModel;
              return (
                <div
                  key={label}
                  className={classNames(styles.imgLabelContainer, isItemSelected(label) ? styles.active : '')}
                  onClick={() => {
                    // 检查是否需要登录
                    if (label?.toLocaleLowerCase() === 'my followed' && !isLogin && onLoginRequired) {
                      onLoginRequired();
                      return;
                    }
                    if (!isItemSelected(label)) {
                      onItemClick(label);
                    }
                  }}
                >
                  <Image
                    className={styles.img}
                    src={imgUrl(imageItem?.image, 'small')}
                    alt={label}
                    width={30}
                    height={30}
                    loading="lazy"
                  />
                  <span>{label}</span>
                </div>
              );
            } else {
              // 文本按钮样式标签
              return (
                <Button
                  key={label}
                  className={classNames(styles.label, isItemSelected(label) ? styles.active : '')}
                  onClick={() => {
                    if (!isItemSelected(label)) {
                      onItemClick(label);
                    }
                  }}
                >
                  {label}
                </Button>
              );
            }
          })}
          {layoutMode !== 'scroll' &&
            showMoreButton &&
            labels?.length > maxDisplayCount &&
            (type === 'image' ? (
              <div className={classNames(styles.imgLabelContainer)} onClick={onMoreClick}>
                <Image className={styles.img} src={''} alt={''} width={0} height={30} loading="lazy" />
                <span>{'More'}</span>
              </div>
            ) : (
              <Button className={classNames(styles.label)} onClick={onMoreClick}>
                {'More'}
              </Button>
            ))}
        </div>
      </div>
    </>
  );
};

export default LabelFilterArea;
